import { type Cell, type Row as TanstackRow, flexRender } from '@tanstack/react-table';
import styled from '@emotion/styled';
import type { VirtualItem } from '@tanstack/react-virtual';
import { Warning } from '@phosphor-icons/react';
import { TableTooltip } from './TableTooltip';
import { useTheme } from '@emotion/react';
import { TableConfig, TableEditableState, TableRowBackgroundContext } from '../../types';
import { stringParse } from 'utils';
import {
  isCellEditable,
  isTaskRowUnreadForRow,
  resolveTableRowCellBackgroundColor,
} from '../../utils';
import { IconButton } from 'components/button';
import { Link } from 'react-router-dom';
import { memo, useMemo } from 'react';
import { useStableContext } from './OptimizedTableProvider';

// ── Module-level constant styles (no theme dependency to avoid init order issues) ──
const FLEX_GROW_STYLE = { flexGrow: 1 } as const;
const SUB_ROW_BORDER_STYLE = { borderTop: 'none' } as const;
const OPACITY_INVALID = 0.3;

export type VisibleColumnRange = { startIndex: number; endIndex: number } | null;

export type RowProps = {
  row: TanstackRow<unknown>;
  rowIndex: number;
  virtualItem: VirtualItem;
  tooltipDelay?: number;
  loggedInUser?: { id: string | number } | null;
  config?: TableConfig;
  editableState?: TableEditableState;
  tableWidth?: number;
  getInvalidRow?: (row?: unknown) => boolean;
  visibleColumnRange?: VisibleColumnRange;
  isSelected?: boolean;
};

type RowType = {
  invalid?: boolean;
  viewedOn?: Date | null;
};

const RowInner = ({
  row,
  rowIndex,
  virtualItem,
  tooltipDelay = 100,
  loggedInUser,
  config = {} as TableConfig,
  editableState,
  tableWidth = 0,
  getInvalidRow,
  visibleColumnRange = null,
  isSelected = false,
}: RowProps) => {
  const { variant = 'default', expandedRowBackgroundColor, getRowBackground } = useStableContext();
  const isSelectedClass = isSelected ? 'selected' : '';
  const currentRow = row?.original as RowType;
  const theme = useTheme();

  const isAnyCellEditing = editableState?.rowIndex != null;

  const isTaskUnread = isTaskRowUnreadForRow(row, loggedInUser?.id);

  const isInvalidRow = currentRow?.invalid ?? getInvalidRow?.(row?.original);
  const isSubRow = row?.depth > 0;

  type RowBgCtx = TableRowBackgroundContext<unknown>;

  const cellBackgroundColor = useMemo(
    () =>
      resolveTableRowCellBackgroundColor({
        row,
        isSelected: Boolean(row?.getIsSelected()),
        isTaskUnread,
        isInvalidRow: Boolean(isInvalidRow),
        variant,
        themeBgPrimary: theme.palette.bg.primary,
        expandedRowBackgroundColor,
        getRowBackground: getRowBackground as ((ctx: RowBgCtx) => string | undefined) | undefined,
      }),
    [
      row,
      row.getIsSelected(),
      row.getIsExpanded(),
      expandedRowBackgroundColor,
      getRowBackground,
      isTaskUnread,
      isInvalidRow,
      variant,
      theme.palette.bg.primary,
    ]
  );

  const borderStyles = useMemo(() => {
    const subtle = theme.palette.border.primarySubtle;
    return {
      left: { borderLeft: `0.5px solid ${subtle}` },
      right: { borderRight: `0.5px solid ${subtle}` },
      both: {
        borderLeft: `0.5px solid ${subtle}`,
        borderRight: `0.5px solid ${subtle}`,
      },
    };
  }, [theme.palette.border.primarySubtle]);

  // Memoize the tr style object
  const trStyle = useMemo(
    () => ({
      ...(isSubRow ? SUB_ROW_BORDER_STYLE : undefined),
      height: `${virtualItem.size}px`,
      transform: `translateY(${virtualItem.start}px)`,
      zIndex: isSubRow ? 1 : 2,
      minWidth: tableWidth,
    }),
    [isSubRow, virtualItem.size, virtualItem.start, tableWidth]
  );

  return (
    <tr
      data-testid={`${config?.tableId}_table_row_${rowIndex}`.toLowerCase()}
      className={isSelectedClass}
      data-row-index={rowIndex}
      style={trStyle}
    >
      {row?.getVisibleCells().map((cell, index) => {
        const isFrozen = cell.column.getIsPinned();
        const left = isFrozen ? cell?.column?.getStart('left') : 0;
        const isLastCell = index === row.getVisibleCells()?.length - 1;
        const isEditable = isCellEditable(cell.column.columnDef.meta?.editable, cell);
        const tooltipRender = cell.column.columnDef.tooltipRender;
        const border = cell.column.columnDef.border;
        const cellClick = cell.column.columnDef.meta?.onCellClick;
        const isSelector = cell.column.id === 'Selector';
        const primary = cell.column.columnDef.meta?.primary;

        const isInVisibleRange =
          visibleColumnRange == null ||
          isFrozen ||
          (index >= visibleColumnRange.startIndex && index <= visibleColumnRange.endIndex);

        const tooltipTitle = tooltipRender?.(cell);
        const shouldRenderTooltip = Boolean(
          tooltipRender && !isInvalidRow && tooltipTitle && !isAnyCellEditing
        );

        const cellStyle: React.CSSProperties = {
          left,
          width: cell.column.getSize(),
          minWidth: cell.column.getSize(),
          ...(isLastCell ? FLEX_GROW_STYLE : undefined),
          ...(border === 'left' ? borderStyles.left : undefined),
          ...(border === 'right' ? borderStyles.right : undefined),
          ...(border === 'both' ? borderStyles.both : undefined),
          ...(cellBackgroundColor ? { backgroundColor: cellBackgroundColor } : {}),
          opacity: isInvalidRow ? OPACITY_INVALID : undefined,
        };

        return (
          <CellDiv
            data-testid={`${config?.tableId}_table_cell_${cell?.id}`.toLowerCase()}
            key={cell.id}
            className={`${isFrozen ? 'frozen ' : ''}${isSelector ? 'selector ' : ''}${
              row?.getIsSelected() ? 'selected' : ''
            }${isTaskUnread ? ' unread' : ''}`}
            isEditable={isEditable}
            noPadding={cell.column.columnDef.meta?.noPadding}
            bold={cell.column.columnDef.meta?.bold || Boolean(primary)}
            onClick={() => {
              if (!isSelector && cellClick && !isEditable) {
                cellClick(cell);
              }
            }}
            style={cellStyle}
          >
            {!isInVisibleRange ? null : (
              <CellContentReveal>
                {shouldRenderTooltip ? (
                  <TableTooltip
                    key={cell.id}
                    title={tooltipTitle}
                    disableFocusListener
                    disableHoverListener={false}
                    disableInteractive
                    enterDelay={tooltipDelay}
                    enterNextDelay={tooltipDelay}
                  >
                    <CellRender
                      cell={cell}
                      config={config}
                      index={index}
                      invalid={isInvalidRow}
                      primary={primary}
                      isSelected={isSelector ? isSelected : undefined}
                    />
                  </TableTooltip>
                ) : (
                  <CellRender
                    cell={cell}
                    config={config}
                    index={index}
                    invalid={isInvalidRow}
                    primary={primary}
                    isSelected={isSelector ? isSelected : undefined}
                  />
                )}
              </CellContentReveal>
            )}
          </CellDiv>
        );
      })}
    </tr>
  );
};

RowInner.displayName = 'Row';

/**
 * Custom areEqual for Row memo.
 * Shallow-compares all props; for virtualItem, compares start + size individually.
 * When editableState changes, only the row that entered edit or left edit re-renders.
 */
function rowAreEqual(prev: RowProps, next: RowProps): boolean {
  if (prev.row !== next.row) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.tooltipDelay !== next.tooltipDelay) return false;
  if (prev.loggedInUser !== next.loggedInUser) return false;
  if (prev.config !== next.config) return false;
  if (prev.tableWidth !== next.tableWidth) return false;
  if (prev.getInvalidRow !== next.getInvalidRow) return false;
  if (prev.visibleColumnRange !== next.visibleColumnRange) return false;
  // Compare virtualItem fields individually instead of by reference
  const pv = prev.virtualItem;
  const nv = next.virtualItem;
  if (pv.index !== nv.index || pv.start !== nv.start || pv.size !== nv.size) return false;
  // When editableState changes, re-render only the row that entered or left edit
  if (prev.editableState !== next.editableState) {
    const editingRow = next.editableState?.rowIndex ?? -1;
    const prevEditingRow = prev.editableState?.rowIndex ?? -1;
    if (prev.rowIndex !== editingRow && prev.rowIndex !== prevEditingRow) return true;
    return false;
  }
  return true;
}

export const Row = memo(RowInner, rowAreEqual);

type CellRenderProps = {
  cell: Cell<unknown, unknown>;
  index: number;
  invalid?: boolean;
  config: TableConfig;
  primary?:
    | true
    | {
        icon: React.ReactElement;
        tooltip: string;
        onClick?: () => void;
        link?: string;
        vars?: string[];
      }[];
  isSelected?: boolean; // Triggers re-render for selector cell when selection changes
};

const CellRenderInner = ({
  invalid,
  index,
  cell,
  config,
  primary,
  isSelected,
}: CellRenderProps) => {
  if (invalid && index === 1) {
    return (
      <Icon>
        <TableTooltip title={config?.texts?.invalidRow}>
          <Warning size={24} />
        </TableTooltip>{' '}
        <TableTooltip title={cell?.column?.columnDef?.tooltipRender?.(cell) ?? ''}>
          <span>{flexRender(cell?.column?.columnDef?.cell, cell?.getContext()) ?? ''}</span>
        </TableTooltip>
      </Icon>
    );
  }
  if (Array.isArray(primary)) {
    return (
      <PrimaryButtonContainer>
        {flexRender(cell?.column?.columnDef?.cell, cell?.getContext())}
        {primary.map(p => {
          if (p.link) {
            let url = p.link;
            if (p.vars?.length) {
              const row = cell.row.original as Record<string, unknown>;
              url = stringParse(
                p.link,
                p.vars.map(v => String(row[v] ?? ''))
              );
            }
            return (
              <Link to={url} key={p.tooltip}>
                <IconButton className="primary-buttons">
                  <TableTooltip title={p.tooltip}>{p.icon}</TableTooltip>
                </IconButton>
              </Link>
            );
          }
          if (p.onClick) {
            return (
              <IconButton className="primary-buttons" key={p.tooltip} onClick={p.onClick}>
                <TableTooltip title={p.tooltip}>{p.icon}</TableTooltip>
              </IconButton>
            );
          }
          return null;
        })}
      </PrimaryButtonContainer>
    );
  }
  return flexRender(cell?.column?.columnDef?.cell, cell?.getContext());
};

CellRenderInner.displayName = 'CellRender';

const CellRender = memo(CellRenderInner);

const PrimaryButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  > span:first-of-type {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  button {
    display: none;
    flex-shrink: 0;
  }
`;

const Icon = styled.span`
  && {
    gap: 8px;
    display: flex;
    align-items: center;
    > svg {
      flex-shrink: 0;
    }
    > a,
    div,
    span {
      overflow: hidden;
    }
  }
`;

const CellContentReveal = styled.div`
  @keyframes cellRevealFadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: cellRevealFadeIn 0.18s ease-out;
  width: 100%;
  min-width: 0;
`;

export const CellDiv = styled.td<{
  isEditable?: boolean;
  noPadding?: boolean;
  bold?: boolean;
}>`
  && {
    cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
    ${p => (p.noPadding ? 'padding: 0;' : '')}
    ${p => (p.bold ? `font-weight: ${p.theme.typography.weight.semiBold};` : '')}
  }
  .dotsMenuBtn {
    all: unset;
    cursor: pointer;
  }
  &.selector {
    padding: unset;
  }
  .dotsMenu {
    display: none;
    background-color: transparent;
    padding-top: 1px;
    border-radius: 3px;
    height: 18px;
  }
  :hover {
    .dotsMenu {
      display: flex;
    }
    .primary-buttons {
      display: flex;
    }
  }
`;
